import { create } from "zustand";
import type { Cart, LineType } from "../types/cart";
import { apiDelete, apiGet, apiPost, apiPut } from "@/utils/axiosInstance";

const API = "/cart";

type AddItemBody =
  | { line_type?: Exclude<LineType, "bundle">; product_id: number; variant_id?: number | null; quantity?: number }
  | { line_type: "bundle"; product_id: number; quantity?: number; components: Array<{ product_id: number; variant_id: number | null }> };

interface ShippingMethod {
  id: number;
  title: string;
  description: string;
  price: number;
  is_selected: boolean;
}

interface Coupon {
  id: number;
  code: string;
  discount: number;
  type: "percentage" | "fixed";
  applicable_products: number[];
}

// Declaración de tipos del store
type State = {
  cart2: Cart | null;
  loading: boolean;
  error: string | null;
  shippingMethods: ShippingMethod[];
  fetchCart: () => Promise<void>;
  addItem: (body: AddItemBody) => Promise<void>;
  updateItemQty: (id: number, quantity: number) => Promise<void>;
  removeItem: (id: number) => Promise<void>;
  emptyCart: () => Promise<void>;
  applyCoupon: (code: string) => Promise<void>;
  removeCoupon: () => Promise<void>;
  setShippingMethods: (methods: ShippingMethod[]) => void;
  selectShippingMethod: (methodId: number) => void;
  clearShippingMethods: () => void;
  trackCartEvent: (eventName: string, eventData?: Record<string, any>) => void;
};

export const useCartStore2 = create<State>((set, get) => ({
  cart2: null,
  loading: false,
  error: null,
  shippingMethods: [],

  async fetchCart() {
    set({ loading: true, error: null });
    try {
      const data = await apiGet(`${API}`);

      //console.log("🛒 fetchCart response:", data);
      set({ cart2: data as Cart, loading: false });
      get().trackCartEvent("cart_updated", { source: "fetchCart" });
    } catch (e: any) {
      set({ error: e?.response?.data?.message ?? e.message ?? "Error al obtener carrito", loading: false });
    }
  },

  async addItem(body) {
    set({ loading: true, error: null });
    try {
      const current = get().cart2;
      const isSubscription = body.line_type === "subscription";

      if (isSubscription) {
        const quantity = body.quantity ?? 1;
        if (quantity > 1) {
          body = { ...body, quantity: 1 };
        }

        const existingSub = current?.items?.find((i) => i.line_type === "subscription");
        if (existingSub) {
          if (existingSub.product_id === body.product_id) {
            set({ loading: false });
            get().trackCartEvent("cart_updated", {
              action: "noop_subscription",
              reason: "already_present",
              product_id: existingSub.product_id,
              product_name: existingSub.product?.name,
              quantity: existingSub.quantity,
            });
            return;
          } else {
            const removed = await apiDelete(`${API}/items/${existingSub.id}`);
            const cartAfterRemoval = (removed.cart ?? removed) as Cart;
            set({ cart2: cartAfterRemoval, loading: true });
            get().trackCartEvent("cart_updated", {
              action: "removeSubscription",
              item_id: existingSub.id,
              product_id: existingSub.product_id,
              product_name: existingSub.product?.name,
            });
          }
        }
      }

      const data = await apiPost(`${API}/items`, body);
      //console.log("🛒 addItem response:", data);
      const cart = (data.cart ?? data) as Cart;
      set({ cart2: cart, loading: false });

      // obtener el ítem agregado (para bundle hay componentes, pasamos datos base)
      const added = cart.items.find((i) =>
        body.line_type === "bundle" ? i.line_type === "bundle" && i.product_id === body.product_id : i.product_id === body.product_id && i.variant_id === (body as any).variant_id
      );

      get().trackCartEvent("cart_updated", {
        action: "addItem",
        line_type: body.line_type ?? "single",
        product_id: body.product_id,
        product_name: added?.product?.name,
        quantity: body.quantity ?? added?.quantity,
        variant_id: (body as any).variant_id ?? added?.variant_id ?? undefined,
        variant: added?.variant?.sku ? `SKU: ${added.variant.sku}` : undefined,
        components: added?.components?.map((c) => ({
          product_id: c.component_product_id,
          variant_id: c.component_variant_id,
          product_name: c.product?.name,
          variant: c.variant?.sku ? `SKU: ${c.variant.sku}` : undefined,
          qty_per_bundle: c.quantity_per_bundle,
        })),
      });
    } catch (e: any) {
      set({ error: e?.response?.data?.message ?? e.message ?? "Error al agregar al carrito", loading: false });
    }
  },

  async updateItemQty(id, quantity) {
    set({ loading: true, error: null });
    try {
      const data = await apiPut(`${API}/items/${id}`, { quantity });
      set({ cart2: data as Cart, loading: false });

      const updated = (data as Cart).items.find((i) => i.id === id);
      get().trackCartEvent("cart_updated", {
        action: "updateItemQty",
        item_id: id,
        product_id: updated?.product_id,
        product_name: updated?.product?.name,
        quantity,
        variant_id: updated?.variant_id ?? undefined,
        variant: updated?.variant?.sku ? `SKU: ${updated.variant.sku}` : undefined,
      });
    } catch (e: any) {
      set({ error: e?.response?.data?.message ?? e.message ?? "Error al actualizar cantidad", loading: false });
    }
  },

  async removeItem(id) {
    set({ loading: true, error: null });
    try {
      const data = await apiDelete(`${API}/items/${id}`);
      set({ cart2: data as Cart, loading: false });

    } catch (e: any) {
      set({ error: e?.response?.data?.message ?? e.message ?? "Error al eliminar producto", loading: false });
    }
  },

  async emptyCart() {
    set({ loading: true, error: null });
    try {
      const prev = get().cart2;
      const removedItems = prev?.items?.map((i) => ({
        item_id: i.id,
        product_id: i.product_id,
        product_name: i.product?.name,
        quantity: i.quantity,
        variant_id: i.variant_id ?? undefined,
        variant: i.variant?.sku ? `SKU: ${i.variant.sku}` : undefined,
      }));

      const data = await apiDelete(`${API}`);
      set({ cart2: data as Cart, loading: false });

      get().trackCartEvent("cart_deleted", {
        action: "emptyCart",
        removed_count: prev?.items?.length ?? 0,
        removed_items: JSON.stringify(removedItems ?? []),
        prev_subtotal: prev?.subtotal_amount,
      });
    } catch (e: any) {
      set({ error: e?.response?.data?.message ?? e.message ?? "Error al vaciar carrito", loading: false });
    }
  },

  async applyCoupon(code) {
    set({ loading: true, error: null });
    try {
      const data = await apiPost(`${API}/coupon`, { code });
      //console.log("🛒 applyCoupon response:", data);
      const cart = (data.cart ?? data) as Cart;
      set({ cart2: cart, loading: false });
      get().trackCartEvent("cart_updated", { action: "applyCoupon", code });
      return data;
    } catch (e: any) {
      const errorMessage = e?.response?.data?.message ?? e.message ?? "Error al aplicar cupón";
      set({ error: errorMessage, loading: false });
      throw new Error(errorMessage);
    }
  },

  async removeCoupon() {
    set({ loading: true, error: null });
    try {
      const data = await apiDelete(`${API}/coupon`);
      //console.log("🛒 removeCoupon response:", data);
      const cart = (data.cart ?? data) as Cart;
      set({ cart2: cart, loading: false });
      get().trackCartEvent("cart_updated", { action: "removeCoupon" });
    } catch (e: any) {
      set({ error: e?.response?.data?.message ?? e.message ?? "Error al eliminar cupón", loading: false });
    }
  },

  setShippingMethods: (methods) => {
    set({ shippingMethods: methods });
  },

  selectShippingMethod: (methodId) =>
    set((state) => {
      const newMethods = state.shippingMethods.map((method) => ({
        ...method,
        is_selected: method.id === methodId,
      }));

      return { shippingMethods: newMethods };
    }),

  clearShippingMethods: () => {
    set({ shippingMethods: [] });
  },

  trackCartEvent: (eventName: string, eventData: any) => {
    if (typeof window !== "undefined" && (window as any).trackBrevoEvent) {
      const cart2 = get().cart2 as any
      
      if (!cart2) return;
      
      const cartData = cart2.items.map((item: any) => ({
        id: item.product_id,
        name: item.product?.name || "",
        price: item.unit_amount,
        quantity: item.quantity,
        variant: item.variant_id ? `Variante ID: ${item.variant_id}` : undefined,
        total: item.unit_amount * item.quantity,
      }));
      
      const cartTotal = cart2.subtotal_amount || 0;
      
      const couponData = cart2.coupon
        ? {
            coupon_code: cart2.coupon.code,
            discount_amount: cart2.discount_amount,
          }
        : undefined;
      
      const eventPayload = {
        cart_items: JSON.stringify(cartData),
        cart_total: cartTotal,
        coupon: couponData ? JSON.stringify(couponData) : undefined,
        ...eventData,
      };
      
      (window as any).trackBrevoEvent(eventName, eventPayload);
    }
  },

  orderCompleted: (eventData: any) => {
    get().trackCartEvent("order_completed", eventData);
  },
}));
